import React, { useState } from "react";
import axios from "axios";
import { X, Send, Loader2 } from "lucide-react";
import { getAuthHeaders } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// "¿Querés avisar a [nombre]?" — shown right after an employer moves a candidate
// into a stage that implies real communication (contacted / hired / rejected).
// Interview scheduling has its own dedicated InterviewFormModal — it never
// routes through this generic message-only flow anymore.
function FieldBlock({ label, className = "", children }) {
  return (
    <div className={className}>
      <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">{label}</label>
      {children}
    </div>
  );
}

export default function NotifyApplicantModal({ application, stage, onClose, onSent }) {
  const { t } = useLanguage();
  const template = t(`notifyApplicant.template.${stage}`)
    .replace("{name}", application.name || "")
    .replace("{job}", application.job_title || "");
  const [message, setMessage] = useState(template);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const send = async () => {
    setSending(true);
    setError("");
    try {
      const res = await axios.post(`${API}/applications/${application.application_id}/notify`, { stage, message }, { headers: getAuthHeaders() });
      onSent(res.data.notification);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail?.message || err.response?.data?.detail || t("notifyApplicant.sendError"));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-end sm:items-center justify-center" data-testid="notify-applicant-modal" onClick={onClose}>
      <div className="bg-white w-full max-h-[92dvh] rounded-t-2xl sm:max-h-[85vh] sm:max-w-lg sm:rounded-2xl flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-slate-100 flex-shrink-0">
          <h3 className="font-poppins font-bold text-base text-slate-900" data-testid="notify-applicant-title">
            {t("notifyApplicant.title").replace("{name}", application.name || "")}
          </h3>
          <button onClick={onClose} data-testid="notify-applicant-close" className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors flex-shrink-0">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          <FieldBlock label={t("notifyApplicant.message")}>
            <textarea rows={5} data-testid="notify-applicant-message" value={message} onChange={(e) => setMessage(e.target.value)}
              className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#028CF8]/20 focus:border-[#028CF8] resize-none" />
          </FieldBlock>

          {error && <p className="text-sm text-red-600" data-testid="notify-applicant-error">{error}</p>}
        </div>

        <div className="flex items-center justify-between gap-3 px-5 py-4 border-t border-slate-100 flex-shrink-0" style={{ paddingBottom: "calc(16px + env(safe-area-inset-bottom))" }}>
          <button onClick={onClose} data-testid="notify-applicant-skip" className="h-11 px-4 rounded-xl border border-slate-200 text-slate-600 text-sm font-semibold hover:bg-slate-50 transition-colors">
            {t("notifyApplicant.skip")}
          </button>
          <button onClick={send} disabled={sending || !message.trim()} data-testid="notify-applicant-send"
            className="flex items-center gap-2 h-11 px-5 rounded-xl bg-[#028CF8] hover:bg-[#0270C5] text-white text-sm font-semibold transition-all disabled:opacity-50">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} {t("notifyApplicant.send")}
          </button>
        </div>
      </div>
    </div>
  );
}
